import React, { useState, useEffect, useRef } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import {
  Container,
  Typography,
  Grid,
  CircularProgress,
} from '@mui/material';
import { fetchBlogs } from '../api';
import { BLUE_PLOOT } from '@plootus/common';
import BlogSearch from './BlogSearch';
import BlogCard from './BlogCard';
import BlogPagination from './BlogPagination';

const useStyles = makeStyles((theme) => ({
  root: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(6),
  },
  title: {
    fontSize: '1.875rem',
    fontWeight: 700,
    color: '#1E293B',
    marginBottom: theme.spacing(4),
  },
  gridContainer: {
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(4),
  },
  loaderContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 400,
  },
}));

const AllBlogPosts = () => {
  const classes = useStyles();
  const [blogPosts, setBlogPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const postsPerPage = 12;
  const sectionRef = useRef(null);

  useEffect(() => {
    const load = async () => {
      try {
        const posts = await fetchBlogs();
        const formatted = posts.map((b) => ({
          id: b.id,
          slug: b.slug || String(b.id),
          title: b.title || b.heading,
          excerpt: b.excerpt?.length ? b.excerpt : (b.description || '').slice(0, 150) + ((b.description || '').length > 150 ? '...' : ''),
          image: b.image || b.imageUrl || '/placeholder.png',
          date: formatDate(b.date || new Date().toISOString()),
          link: b.link || null,
          youtubeUrl: b.youtubeUrl || null,
        }));
        setBlogPosts(formatted);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching blogs:', error);
        setLoading(false);
      }
    };
    load();
  }, []);

  // Helper functions for content extraction
  // no HTML extraction needed when using backend data

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  };

  // Filter posts based on search query
  const filteredPosts = blogPosts.filter(
    (post) =>
      post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.excerpt.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Calculate pagination
  const indexOfLastPost = currentPage * postsPerPage;
  const indexOfFirstPost = indexOfLastPost - postsPerPage;
  const currentPosts = filteredPosts.slice(indexOfFirstPost, indexOfLastPost);
  const totalPages = Math.ceil(filteredPosts.length / postsPerPage);

  if (loading) {
    return (
      <div className={classes.root}>
        <Container maxWidth="lg">
          <Typography variant="h2" className={classes.title}>
            All Blog Posts
          </Typography>
          <div className={classes.loaderContainer}>
            <CircularProgress style={{ color: BLUE_PLOOT }} size={40} />
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className={classes.root} ref={sectionRef}>
      <Container maxWidth="lg">
        {/* Section Title */}

        {/* Search Bar */}
        <BlogSearch searchQuery={searchQuery} onSearchChange={setSearchQuery} />

        {/* Blog Grid */}
        <Grid container spacing={4} className={classes.gridContainer}>
          {currentPosts.map((post) => (
            <Grid item xs={12} sm={6} md={4} key={post.id}>
              <BlogCard post={post} />
            </Grid>
          ))}
        </Grid>

        {/* Pagination */}
        {filteredPosts.length > postsPerPage && (
          <BlogPagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={(page) => {
              setCurrentPage(page);
              requestAnimationFrame(() => {
                const el = sectionRef.current;
                if (el) {
                  const top = el.getBoundingClientRect().top + window.scrollY - 80;
                  window.scrollTo({ top: top < 0 ? 0 : top, behavior: 'auto' });
                }
              });
            }}
          />
        )}
      </Container>
    </div>
  );
};

export default AllBlogPosts;
